/**
 * Working out which singer sings which track of a duet, from the only thing the song file says
 * about it: the artist credit.
 *
 * UltraStar carries `#P1:`/`#P2:` for this and `convert-txt-to-song` already reads them, but only
 * 56 of the 123 multitrack songs in the catalogue have them filled in - the rest render
 * "Track 1"/"Track 2", which tells a room nothing about who should sing which.
 *
 * Lives in `src/` rather than beside `backfill-track-singers`, which is where it started: the same
 * rule now answers a screen as well as that backfill (see `trackLabel`), and a song converted or
 * shared after the backfill ran has never been through it at all.
 *
 * Nothing here guesses. A credit either names exactly as many performers as the song has tracks, in
 * which case they map in order, or it does not and the song is left alone. "Take That" is two
 * tracks of one band, not two singers, and naming both tracks "Take That" would be worse than
 * numbering them.
 */

/**
 * Credit separators. Deliberately past English: `y` is Spanish, `e` Portuguese, `ja` Finnish and
 * Polish, `und` German, `med` Swedish, `oraz` Polish. An English-only list left
 * "Christian Nodal y Ángela Aguilar" and "Diogo Piçarra e Pedro Abrunhosa" looking like one name.
 */
const SEPARATORS = /\s*(?:\s(?:feat\.|ft\.|featuring|vs\.?|and|with|y|e|ja|und|med|oraz|x)\s|\s*[&+,]\s*)\s*/i;

/** A trailing parenthetical is the release, not a performer - "(Moulin Rouge)", "(Live)". */
const TRAILING_PARENTHETICAL = /\s*\([^)]*\)\s*$/;

const clean = (part: string) => part.replace(TRAILING_PARENTHETICAL, '').trim();

const split = (source: string) =>
  source
    .split(SEPARATORS)
    .map(clean)
    .filter((part) => part.length > 0);

/**
 * The performers a credit names, in the order it names them. Two shapes are read: the credit itself
 * ("Eminem feat. Rihanna"), and a trailing parenthetical listing the cast, which is how this
 * catalogue credits musicals - "Glee Cast (Lea Michele & Jake Zyrus)", where "Glee Cast" is the act
 * and the names inside it are the singers.
 *
 * A parenthetical only counts as a cast when it names more than one performer. That is the whole
 * difference between "(Lea Michele & Jake Zyrus)" and "(Moulin Rouge)", which is a release.
 */
export const performersIn = (credit: string): string[] => {
  const parenthetical = credit.match(/\(([^)]*)\)\s*$/);
  const cast = parenthetical ? split(parenthetical[1]) : [];

  return cast.length > 1 ? cast : split(credit);
};

/**
 * What to call one track of a duet, for the pickers that ask which track a singer is taking.
 *
 * `tracks[index].name` is the song file's own `#P1:`/`#P2:` and always wins - it is either curated
 * or was written by `backfill-track-singers`, and a hand-written answer beats a derived one. Failing
 * that the credit is read here, so "Drake" beats "Track 1" on a duet whose file was never given the
 * headers, and `trackSingers` refusing is what keeps a band or a three-name credit numbered instead
 * of labelled with a guess.
 */
export const trackLabel = (
  song: { artist: string; tracksCount: number; tracks: Array<{ name?: string }> },
  index: number,
): string => {
  const named = song.tracks[index]?.name;
  if (named) return named;

  return trackSingers(song.artist, song.tracksCount)?.[index] ?? `Track ${index + 1}`;
};

/**
 * Names for a song's tracks, or `null` when the credit cannot say. Requires one distinct performer
 * per track: three names on two tracks is a credit for the recording rather than for the tracks,
 * and "Klaus & Klaus" would label both tracks the same.
 */
export const trackSingers = (credit: string, trackCount: number): string[] | null => {
  const performers = performersIn(credit);

  if (performers.length !== trackCount) return null;
  if (new Set(performers).size !== performers.length) return null;

  return performers;
};
